import React from 'react';
import { School, Heart, Users, ShoppingBag, Utensils, Book, MapPin, Phone, Globe, Bus, Wifi, Droplets } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';

// Real Cornwall education data
const schools = [
  {
    name: "St. Lawrence College",
    type: "College",
    description: "Post-secondary institution offering 100+ programs. Main campus in Cornwall with satellite locations. Strong co-op and apprenticeship programs.",
    stats: { students: "6,500+", programs: "100+", founded: "1967" },
    programs: ["Business", "Health Sciences", "Technology", "Trades", "Community Services", "Tourism"],
    website: "www.stlawrencecollege.ca",
    address: "2 St. Lawrence Dr, Cornwall"
  },
  {
    name: "Upper Canada District School Board",
    type: "Public School Board",
    description: "English public schools serving the region with 24 elementary schools and 3 secondary schools in Cornwall.",
    stats: { schools: "27", students: "12,000+", region: "Eastern Ontario" },
    programs: ["French Immersion", "Specialist High Skills Major", "International Baccalaureate", "Special Education"],
    website: "www.ucdsb.on.ca",
    address: "225 Central Ave W, Brockville"
  },
  {
    name: "Catholic District School Board of Eastern Ontario",
    type: "Catholic School Board",
    description: "Catholic education with strong academic results and faith-based curriculum.",
    stats: { schools: "12", students: "5,000+", region: "SD&G and Prescott-Russell" },
    programs: ["Faith Formation", "French Immersion", "Technology", "Arts"],
    website: "www.cdsbeo.on.ca",
    address: "835 Campbell St, Cornwall"
  },
  {
    name: "Conseil des écoles publiques de l'Est de l'Ontario",
    type: "French Public Board",
    description: "French-language public education for the Francophone community.",
    stats: { schools: "3", students: "1,200+", language: "French" },
    programs: ["Full French curriculum", "Arts", "Sciences", "Technology"],
    website: "www.cepeo.on.ca",
    address: "Cornwall area schools"
  }
];

// Real Cornwall healthcare data
const healthcare = [
  {
    name: "Cornwall Community Hospital",
    type: "Regional Hospital",
    services: ["24/7 Emergency", "Surgery", "Maternity & Birthing", "ICU/CCU", "Diagnostic Imaging", "Cancer Care", "Mental Health", "Rehabilitation"],
    description: "Full-service regional hospital serving 165,000 people in SD&G and Akwesasne. Recently expanded cancer care and diagnostic services.",
    stats: { beds: "140", staff: "1,800+", er_visits: "50,000/year" },
    address: "840 McConnell Ave, Cornwall",
    phone: "(613) 938-4240"
  },
  {
    name: "Cornwall Community Health Centre",
    type: "Community Health",
    services: ["Primary Care", "Diabetes Education", "Mental Health", "Physiotherapy", "Foot Care", "Health Promotion"],
    description: "Provides primary care and health promotion services, especially for underserved populations.",
    stats: { patients: "8,000+", programs: "20+" },
    address: "205 Second St E, Cornwall",
    phone: "(613) 936-0306"
  },
  {
    name: "Seaway Valley Community Health Centre",
    type: "Primary Care",
    services: ["Family Medicine", "Nurse Practitioners", "Social Work", "Dietitian", "Chronic Disease Management"],
    description: "Team-based primary care with focus on health promotion and disease prevention.",
    stats: { physicians: "15+", staff: "50+" },
    address: "353 Pitt St, Cornwall",
    phone: "(613) 930-4892"
  }
];

// Real Cornwall recreation data
const recreation = [
  {
    category: "Sports & Fitness Facilities",
    facilities: [
      { name: "Cornwall Aquatic Centre", description: "Olympic-size pool, water slides, hot tub, fitness area" },
      { name: "Benson Centre", description: "3 NHL-size ice pads, walking track, meeting rooms" },
      { name: "Cornwall Civic Complex", description: "Arena, convention centre, home of Colts hockey" },
      { name: "Cornwall Golf & Country Club", description: "18-hole championship course" },
      { name: "Summerheights Golf Links", description: "18-hole public course" }
    ]
  },
  {
    category: "Arts & Culture",
    facilities: [
      { name: "Cornwall Community Museum", description: "Local history from 1784 to present" },
      { name: "Aultsville Theatre", description: "500-seat performing arts venue at St. Lawrence College" },
      { name: "Cornwall Public Library", description: "Main branch + 2 branches, extensive digital resources" },
      { name: "Art galleries", description: "Multiple galleries featuring local and regional artists" },
      { name: "Lift-Off Festival", description: "Annual hot air balloon festival (August)" }
    ]
  },
  {
    category: "Parks & Nature",
    facilities: [
      { name: "Lamoureux Park", description: "Premier waterfront park with beach, splash pad, bandshell" },
      { name: "Guindon Park", description: "Conservation area with trails, cross-country skiing" },
      { name: "Gray's Creek Conservation", description: "Wetlands, bird watching, nature trails" },
      { name: "80+ municipal parks", description: "Playgrounds, sports fields, green spaces citywide" },
      { name: "45 km waterfront trail", description: "Continuous paved trail along St. Lawrence" }
    ]
  }
];

// Real Cornwall services data
const services = [
  {
    category: "Shopping",
    icon: ShoppingBag,
    items: [
      { name: "Cornwall Square", description: "Downtown mall with 60+ stores" },
      { name: "Brookdale Centre", description: "Big box stores: Walmart, Canadian Tire, etc." },
      { name: "Eastcourt Mall", description: "East end shopping plaza" },
      { name: "Downtown Pitt Street", description: "Local boutiques, restaurants, services" },
      { name: "Cornwall Farmers' Market", description: "Local produce, crafts (Saturdays)" }
    ]
  },
  {
    category: "Dining",
    icon: Utensils,
    items: [
      { name: "100+ restaurants", description: "Diverse cuisines from fine dining to fast casual" },
      { name: "Pitt Street dining", description: "Downtown restaurant row" },
      { name: "Waterfront patios", description: "Seasonal dining with river views" },
      { name: "Local breweries", description: "Craft beer scene growing" },
      { name: "French Canadian cuisine", description: "Traditional Québécois food" }
    ]
  },
  {
    category: "Transportation",
    icon: Bus,
    items: [
      { name: "Cornwall Transit", description: "7 bus routes, $2.50 fare, accessible buses" },
      { name: "Highway 401", description: "Direct access to Montreal (1hr) and Toronto (4.5hr)" },
      { name: "VIA Rail", description: "Station in nearby Casselman (20 min)" },
      { name: "Cornwall Regional Airport", description: "General aviation, charters" },
      { name: "Cornwall-Massena Bridge", description: "15 min to US border" }
    ]
  },
  {
    category: "Utilities & Internet",
    icon: Wifi,
    items: [
      { name: "Hydro One / Cornwall Electric", description: "Reliable electricity" },
      { name: "Enbridge Gas", description: "Natural gas available citywide" },
      { name: "Cornwall Water Services", description: "Clean municipal water" },
      { name: "High-speed internet", description: "Fiber optic from Bell, Cogeco, others" },
      { name: "5G mobile coverage", description: "All major carriers" }
    ]
  }
];

export const CommunityGuidePage = () => {
  return (
    <section className="pt-24 pb-20 bg-gradient-to-br from-slate-50 to-purple-50 min-h-screen" data-testid="community-guide-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 space-y-4">
          <div className="inline-flex items-center space-x-2 bg-purple-100 px-4 py-2 rounded-full mb-4">
            <Users className="w-5 h-5 text-purple-700" />
            <span className="text-sm font-semibold text-purple-700">Living in Cornwall</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900">
            Community Guide
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Everything you need to know about education, healthcare, recreation, and daily life in Cornwall
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <Card className="border-2 border-blue-200">
            <CardContent className="pt-4 pb-4 text-center">
              <div className="text-2xl font-bold text-blue-600">47,000+</div>
              <div className="text-xs text-slate-600">Population</div>
            </CardContent>
          </Card>
          <Card className="border-2 border-green-200">
            <CardContent className="pt-4 pb-4 text-center">
              <div className="text-2xl font-bold text-green-600">60%</div>
              <div className="text-xs text-slate-600">Bilingual</div>
            </CardContent>
          </Card>
          <Card className="border-2 border-purple-200">
            <CardContent className="pt-4 pb-4 text-center">
              <div className="text-2xl font-bold text-purple-600">30+</div>
              <div className="text-xs text-slate-600">Schools</div>
            </CardContent>
          </Card>
          <Card className="border-2 border-red-200">
            <CardContent className="pt-4 pb-4 text-center">
              <div className="text-2xl font-bold text-red-600">200+</div>
              <div className="text-xs text-slate-600">Physicians</div>
            </CardContent>
          </Card>
        </div>

        {/* Interactive Map */}
        <Card className="border-2 border-purple-200 mb-12">
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2">
              <MapPin className="w-5 h-5 text-purple-600" />
              Cornwall Community Map
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="aspect-video w-full rounded-lg overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d44856.84144428736!2d-74.77!3d45.02!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4ccc17e8a6c5e8d7%3A0x5c5d3e39f6a9c5b8!2sCornwall%2C%20ON!5e0!3m2!1sen!2sca!4v1640000000000!5m2!1sen!2sca"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '300px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Cornwall Community Map"
              ></iframe>
            </div>
          </CardContent>
        </Card>

        {/* Tabs for Different Sections */}
        <Tabs defaultValue="education" className="w-full">
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 mb-8 bg-white border-2 border-purple-200 p-1 h-auto">
            <TabsTrigger value="education" className="flex items-center gap-2 py-3">
              <School className="w-4 h-4" />
              <span className="hidden sm:inline">Education</span>
            </TabsTrigger>
            <TabsTrigger value="healthcare" className="flex items-center gap-2 py-3">
              <Heart className="w-4 h-4" />
              <span className="hidden sm:inline">Healthcare</span>
            </TabsTrigger>
            <TabsTrigger value="recreation" className="flex items-center gap-2 py-3">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Recreation</span>
            </TabsTrigger>
            <TabsTrigger value="services" className="flex items-center gap-2 py-3">
              <ShoppingBag className="w-4 h-4" />
              <span className="hidden sm:inline">Services</span>
            </TabsTrigger>
          </TabsList>

          {/* Education Tab */}
          <TabsContent value="education">
            <div className="space-y-6">
              <Card className="border-2 border-blue-200">
                <CardHeader>
                  <CardTitle className="text-2xl">Education in Cornwall</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700 mb-4">
                    Cornwall offers comprehensive education from early childhood through post-secondary, with both English and French language options. The city is served by four school boards and St. Lawrence College.
                  </p>
                  <div className="grid md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg text-center">
                      <div className="text-3xl font-bold text-blue-600 mb-1">4</div>
                      <div className="text-sm text-slate-700">School Boards</div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg text-center">
                      <div className="text-3xl font-bold text-green-600 mb-1">30+</div>
                      <div className="text-sm text-slate-700">Schools</div>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg text-center">
                      <div className="text-3xl font-bold text-purple-600 mb-1">6,500+</div>
                      <div className="text-sm text-slate-700">College Students</div>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg text-center">
                      <div className="text-3xl font-bold text-orange-600 mb-1">100+</div>
                      <div className="text-sm text-slate-700">College Programs</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {schools.map((school, index) => (
                <Card key={index} className="border-2">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-xl">{school.name}</CardTitle>
                        <Badge variant="outline" className="mt-1">{school.type}</Badge>
                      </div>
                      <School className="w-8 h-8 text-blue-600" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-700 mb-4">{school.description}</p>
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-2 text-sm">Programs:</h4>
                        <div className="flex flex-wrap gap-2">
                          {school.programs.map((program, idx) => (
                            <span key={idx} className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded-full">
                              {program}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="text-sm text-slate-600">
                        {Object.entries(school.stats).map(([key, value]) => (
                          <div key={key} className="flex justify-between py-1 border-b last:border-0">
                            <span className="capitalize">{key.replace('_', ' ')}:</span>
                            <span className="font-medium">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <a href={`https://${school.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-600 hover:text-blue-700">
                        <Globe className="w-4 h-4" />
                        Website
                      </a>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Healthcare Tab */}
          <TabsContent value="healthcare">
            <div className="space-y-6">
              <Card className="border-2 border-red-200">
                <CardHeader>
                  <CardTitle className="text-2xl">Healthcare Services</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700 mb-4">
                    Cornwall provides comprehensive healthcare with a regional hospital, multiple clinics, and specialists. The Cornwall Community Hospital serves as the healthcare hub for SD&G.
                  </p>
                  <div className="grid md:grid-cols-4 gap-4">
                    <div className="bg-red-50 p-4 rounded-lg text-center">
                      <div className="text-3xl font-bold text-red-600 mb-1">200+</div>
                      <div className="text-sm text-slate-700">Physicians</div>
                    </div>
                    <div className="bg-pink-50 p-4 rounded-lg text-center">
                      <div className="text-3xl font-bold text-pink-600 mb-1">140</div>
                      <div className="text-sm text-slate-700">Hospital Beds</div>
                    </div>
                    <div className="bg-rose-50 p-4 rounded-lg text-center">
                      <div className="text-3xl font-bold text-rose-600 mb-1">25+</div>
                      <div className="text-sm text-slate-700">Pharmacies</div>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg text-center">
                      <div className="text-3xl font-bold text-orange-600 mb-1">165K</div>
                      <div className="text-sm text-slate-700">Catchment Area</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {healthcare.map((facility, index) => (
                <Card key={index} className="border-2">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-xl">{facility.name}</CardTitle>
                        <Badge variant="outline" className="mt-1">{facility.type}</Badge>
                      </div>
                      <Heart className="w-8 h-8 text-red-600" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-700 mb-4">{facility.description}</p>
                    <div className="mb-4">
                      <h4 className="font-semibold text-slate-900 mb-2 text-sm">Services:</h4>
                      <div className="flex flex-wrap gap-2">
                        {facility.services.map((service, idx) => (
                          <span key={idx} className="text-xs px-2 py-1 bg-red-50 text-red-700 rounded-full">
                            {service}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {facility.address}
                      </span>
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        {facility.phone}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Recreation Tab */}
          <TabsContent value="recreation">
            <div className="space-y-6">
              <Card className="border-2 border-green-200">
                <CardHeader>
                  <CardTitle className="text-2xl">Recreation & Culture</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700 mb-4">
                    Cornwall offers abundant recreational facilities and cultural activities year-round. From waterfront trails to indoor arenas, there's something for everyone.
                  </p>
                  <div className="grid md:grid-cols-4 gap-4">
                    <div className="bg-green-50 p-4 rounded-lg text-center">
                      <div className="text-3xl font-bold text-green-600 mb-1">80+</div>
                      <div className="text-sm text-slate-700">Parks</div>
                    </div>
                    <div className="bg-teal-50 p-4 rounded-lg text-center">
                      <div className="text-3xl font-bold text-teal-600 mb-1">45km</div>
                      <div className="text-sm text-slate-700">Trails</div>
                    </div>
                    <div className="bg-emerald-50 p-4 rounded-lg text-center">
                      <div className="text-3xl font-bold text-emerald-600 mb-1">4</div>
                      <div className="text-sm text-slate-700">Arenas</div>
                    </div>
                    <div className="bg-cyan-50 p-4 rounded-lg text-center">
                      <div className="text-3xl font-bold text-cyan-600 mb-1">40+</div>
                      <div className="text-sm text-slate-700">Annual Events</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {recreation.map((section, index) => (
                <Card key={index} className="border-2">
                  <CardHeader>
                    <CardTitle className="text-xl">{section.category}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4">
                      {section.facilities.map((facility, idx) => (
                        <div key={idx} className="bg-slate-50 rounded-lg p-4">
                          <h4 className="font-semibold text-slate-900 mb-1">{facility.name}</h4>
                          <p className="text-sm text-slate-600">{facility.description}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Services Tab */}
          <TabsContent value="services">
            <div className="space-y-6">
              <Card className="border-2 border-orange-200">
                <CardHeader>
                  <CardTitle className="text-2xl">Essential Services</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700">
                    Cornwall provides all essential services and amenities for comfortable modern living, with convenient access to shopping, dining, transportation, and utilities.
                  </p>
                </CardContent>
              </Card>

              {services.map((service, index) => {
                const IconComponent = service.icon;
                return (
                  <Card key={index} className="border-2">
                    <CardHeader>
                      <CardTitle className="text-xl flex items-center gap-2">
                        <IconComponent className="w-6 h-6 text-orange-600" />
                        {service.category}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {service.items.map((item, idx) => (
                          <div key={idx} className="bg-slate-50 rounded-lg p-4">
                            <h4 className="font-semibold text-slate-900 mb-1">{item.name}</h4>
                            <p className="text-sm text-slate-600">{item.description}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};
